<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, navigate, useParams } from 'svelte-navigator';
  import Prism from 'svelte-prismjs';
  import {
    Breadcrumb,
    BreadcrumbItem,
    Button,
    ButtonDropdown,
    Col,
    Container,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    TabContent,
    TabPane,
    Tooltip
  } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';
  import { formatDistanceToNowStrict } from 'date-fns';

  import { BizCardPreview } from '@client/components/bizcard-preview';
  import { Capability } from '@client/components/capability';
  import { Clipboard } from '@client/components/clipboard';
  import { MultiSelectField, TextField } from '@client/components/fields/general';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { MySignature } from '@client/components/my-signature';
  import { NoMatches } from '@client/components/no-matches';
  import { getNotificationsContext } from '@client/components/notifications';
  import { ParamFields } from '@client/components/param-fields';
  import { Porthole } from '@client/components/porthole';
  import { Spinner } from '@client/components/spinner';
  import { Switch } from '@client/components/switch';
  import { Textarea } from '@client/components/textarea';
  import { UserFeatures } from '@client/components/user-features';
  import { UserIntegrations } from '@client/components/user-integrations';
  import { UserNotifications } from '@client/components/user-notifications';

  import { GroupsService } from '@client/services/groups';
  import { SignaturesService } from '@client/services/signatures';
  import { UsersService } from '@client/services/users';

  import { isGccHigh } from '@client/stores/domain-settings';
  import { userStore } from '@client/stores/user';

  import { userMetadata } from '@client/utils/users';

  import { USER_ROLES } from '@client/enums';

  import { formatDistance } from '@common/utils/dates';
  import { generateUUID } from '@common/utils/strings';

  import { DefaultSignatures } from './default-signatures';
  import { DirectorySettings } from './directory-settings';

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  let roles = [];
  let parameters = [];
  let defaultSignatures = {};
  let selectedSyncData = null;
  let copyText = 'label.COPY';

  let updating = false;
  let loadingDirectoryData = false;
  let showBizcardPreview = false;

  const params = useParams();
  const id = `user-roles-${generateUUID()}`;

  let selectedUserRoles = [];
  let assignedGroups = [];

  let readOnly = false;

  let usersRoles = Object.keys(USER_ROLES || {})
    .filter((role) => !USER_ROLES[role].modifier)
    .map((role) => ({
      label: $format(USER_ROLES[role].label.short),
      value: USER_ROLES[role].id,
      checked: false
    }));

  const onCopy = () => {
    copyText = 'label.COPIED';

    setTimeout(() => {
      copyText = 'label.COPY';
    }, 1200);
  };

  const email = `${$params.id}@${$userStore.domain}`;

  const query = createQuery({
    refetchOnWindowFocus: false,
    queryKey: ['edit-users'],
    queryFn: async () => {
      const usermeta = await UsersService.getUserById(email);

      if (!usermeta) {
        navigate(`${basepath}/manage/users`);
        return;
      }

      selectedUserRoles = usersRoles
        .map((config) => {
          if (usermeta.roles && usermeta.roles[config.value]) {
            return {
              label: $format(USER_ROLES[config.value].label.short),
              value: config.value,
              checked: true
            };
          }

          return false;
        })
        .filter(Boolean);

      readOnly = Boolean(usermeta.roles?.viewonly);

      parameters = [...(usermeta.params || [])];

      const response = await GroupsService.findGroupByEmail({ email });

      // Roles without permission on this endpoint get a 405, which APITransport resolves
      // to an `{ status, message }` object — degrade to "no groups" rather than throwing.
      assignedGroups = (Array.isArray(response) ? response : []).map((group) => group.groupname).sort();

      return usermeta;
    }
  });

  const toggle = () => (selectedSyncData = null);

  const onRoleUpdate = (id, items, selected) => {
    selectedUserRoles = selected;
  };

  const notify = (text, error) => {
    addNotification({
      text,
      type: error ? 'error' : 'success',
      autohide: 2500,
      position: 'top-right'
    });
  };

  const onClearCache = async () => {
    const response = await UsersService.clearCache({
      email: user.email
    });

    if (response !== 'Empty') {
      notify($format('label.FAILURE.UPDATE_USER_CACHE'), true);
    } else {
      notify($format('label.SUCCESS.UPDATE_USER_CACHE', { values: { user: user.name } }));
    }
  };

  const onSendReminder = async () => {
    const response = await UsersService.sendReminder({
      emails: [user.email]
    });

    if (response.status === 400) {
      notify($format('label.FAILURE.SEND_REMINDER'), true);
    } else {
      notify($format('label.SUCCESS.SEND_REMINDER', { values: { user: user.name } }));
    }
  };

  const onToggleUserState = async () => {
    updating = true;

    data.removed = !data.removed;

    const response = await UsersService.updateUser({
      email: data.email,
      removed: data.removed,
      updateType: 'removed'
    });

    updating = false;

    if (response.status === 400) {
      notify($format('label.FAILURE.TOGGLE_USER_STATE', { values: { message: response.message } }), true);
    } else {
      notify($format('label.SUCCESS.TOGGLE_USER_STATE_DISABLED', { values: { user: user.name } }));
    }
  };

  const onUpdate = async () => {
    const roles = {};
    const params = [...parameters];

    selectedUserRoles.forEach(({ value }) => (roles[value] = true));

    // viewonly is no longer in the multiselect, so carry it separately or a
    // save would strip it — mailapi replaces the roles object wholesale.
    if (readOnly) roles.viewonly = true;

    updating = true;

    try {
      const response = await UsersService.updateUser({
        ...data,
        roles,
        params,
        updateType: 'params'
      });

      if (response.status >= 400) {
        notify($format('label.FAILURE.UPDATE_USER', { values: { message: response.message } }), true);
        return;
      }

      notify($format('label.SUCCESS.UPDATE', { values: { item: user.name } }));
    } catch (error) {
      notify($format('label.FAILURE.UPDATE_USER', { values: { message: error.message } }), true);
    } finally {
      updating = false;
    }
  };

  const onMouseEnter = () => {
    showBizcardPreview = true;
  };

  const onMouseLeave = () => {
    showBizcardPreview = false;
  };

  let copyingBizcardUrl = false;

  const copyBizcardUrl = (copyFn) => {
    copyingBizcardUrl = true;

    copyFn(data.bizcardurl);

    setTimeout(() => {
      copyingBizcardUrl = false;
    }, 1000);
  };

  $: data = $query.data;
  $: user = data && userMetadata({ ...data, features: $userStore.features });
  $: roles = Object.entries(data?.roles || {}).sort();
  $: inactive = data?.removed;
  $: color = data?.color || 'var(--color-gray-300)';
  $: usersettings = data?.usersettings || {};

  $: isDomainAdmin = selectedUserRoles.some(({ value }) => value === 'domainadmin');
  $: readOnlyApplies =
    !isDomainAdmin &&
    selectedUserRoles.some(({ value }) => value === 'signatures' || value === 'marketing');
</script>

<div class="page">
  <Loader backdrop show={$query.isLoading || $query.isFetching || updating} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/manage/users" use:link>{$format('label.USERS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EDIT_USER')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="8" sm="6" md="6">
          <h1 class="page-title">{user?.name || ''}</h1>
        </Col>
        <Col xs="4" sm="6" md="6" class="text-right">
          <ButtonDropdown class="hide--mobile">
            <DropdownToggle color="secondary" caret>{$format('label.ACTIONS')}</DropdownToggle>
            <DropdownMenu>
              <DropdownItem on:click={onClearCache}>{$format('label.CLEAR_CACHE')}</DropdownItem>
              <DropdownItem divider />
              <DropdownItem on:click={onSendReminder}>{$format('label.SEND_REMINDER')}</DropdownItem>
              <DropdownItem divider />
              <DropdownItem on:click={onToggleUserState}>
                {#if inactive}
                  {$format('label.ENABLE_USER')}
                {:else}
                  {$format('label.DISABLE_USER')}
                {/if}
              </DropdownItem>
            </DropdownMenu>
          </ButtonDropdown>

          <div class="action-divider hide--mobile"></div>

          <Button color="secondary" class="hide--mobile me-2" on:click={() => navigate(`${basepath}/manage/users`)}>
            {$format('label.CANCEL')}
          </Button>

          <Button color="primary" on:click={onUpdate}>{$format('label.UPDATE')}</Button>
        </Col>
      </Row>
    </Container>
  </div>

  {#if !$query.isFetching && user}
    <div class="page-content">
      <Container fluid>
        <Row class="pt-3">
          <div class="user-edit">
            {#if user}
              <div class="user-profile">
                <div class="person">
                  <div class="photo">
                    <Porthole size="xlarge" details={user} />
                    <div class="roles">
                      {#each roles as [role, active]}
                        {#if active}
                          <div
                            id="{id}-{role}"
                            class="role {role}"
                            style="background-color: {USER_ROLES[role].color};"
                          />

                          <Tooltip target="{id}-{role}" placement="top">
                            {$format(USER_ROLES[role].label.long)}
                          </Tooltip>
                        {/if}
                      {/each}
                    </div>
                  </div>
                  <div class="details">
                    <div>{user.name}</div>
                    {#if user.title}
                      <small>{user.title}</small>
                    {/if}
                    <small class="email">{user.email.split('@').shift()}</small>
                  </div>

                  <div class="d-flex justify-content-around gap-1">
                    {#if data?.google?.isAliasSync && data?.google?.primaryEmail_original}
                      <div class="pill alias">
                        <span id="user-alias-{id}">{$format('label.ALIAS')}</span>
                        <Tooltip target="user-alias-{id}" placement="top">
                          {$format('label.ALIAS_OF', {
                            values: { email: data?.google?.primaryEmail_original.toLowerCase() }
                          })}
                        </Tooltip>
                      </div>
                    {/if}

                    {#if data?.office365?.isAliasSync && data?.office365?.mail_original}
                      <div class="pill alias">
                        <span id="user-alias-{id}">{$format('label.ALIAS')}</span>
                        <Tooltip target="user-alias-{id}" placement="top">
                          {$format('label.ALIAS_OF', {
                            values: { email: data?.office365?.mail_original.toLowerCase() }
                          })}
                        </Tooltip>
                      </div>
                    {/if}

                    {#if data?.office365?.userPrincipalName?.toLowerCase() !== data?.office365?.mail?.toLowerCase()}
                      <div class="pill upn">
                        <span id="user-upn-{id}">UPN</span>
                        <Tooltip target="user-upn-{id}" placement="top">
                          {$format('label.UPN_OF', {
                            values: { email: data?.office365?.userPrincipalName.toLowerCase() }
                          })}
                        </Tooltip>
                      </div>
                    {/if}
                  </div>

                  {#if data.bizcardurl}
                    <div
                      class="pill bizcard bg-white py-0 px-1 mb-3"
                      on:mouseenter={() => onMouseEnter('bizcard')}
                      on:mouseleave={() => onMouseLeave('bizcard')}
                      role="button"
                      tabindex="0"
                    >
                      <span>{$format('label.BUSINESS_CARD')}</span>
                      <Clipboard text={data.bizcardurl} let:copy>
                        <span
                          class="copy ps-2"
                          on:click|preventDefault|stopPropagation={() => copyBizcardUrl(copy)}
                          role="button"
                          tabindex="0"
                        >
                          {#if copyingBizcardUrl}
                            <Icon name="check" />
                          {:else}
                            <Icon name="copy" />
                          {/if}
                        </span>
                      </Clipboard>
                      <BizCardPreview bind:show={showBizcardPreview} url={data.bizcardurl} />
                    </div>
                  {/if}

                  {#if data?.featurestamped}
                    <div class="pill" id="last-seen-stamped-{id}">
                      <span>{$format('label.STAMPED')}</span>
                      <span>
                        {formatDistanceToNowStrict(new Date(data.featurestamped), {
                          addSuffix: false,
                          locale: { formatDistance }
                        })}
                      </span>

                      <Tooltip target="last-seen-stamped-{id}" placement="top">
                        {$format('label.SIGNATURE_LAST_SEEN_STAMPTED')}
                        {formatDistanceToNowStrict(new Date(data.featurestamped), { addSuffix: true })}.
                      </Tooltip>
                    </div>
                  {/if}

                  {#if data?.featureoutlookaddin}
                    <div class="pill" id="last-seen-addin-{id}">
                      <span>
                        {$format('label.ADDIN')} v{data.featureoutlookaddinmsg}
                      </span>
                      <span>
                        {formatDistanceToNowStrict(new Date(data.featureoutlookaddin), {
                          addSuffix: false,
                          locale: { formatDistance }
                        })}
                      </span>

                      <Tooltip target="last-seen-addin-{id}" placement="top">
                        {$format('label.SIGNATURE_LAST_SEEN_ADDIN')}
                        {formatDistanceToNowStrict(new Date(data.featureoutlookaddin), {
                          addSuffix: true
                        })}.
                      </Tooltip>
                    </div>
                  {/if}

                  {#if data?.featureoutlookaddin && $isGccHigh}
                    <div class="pill" id="gcc-high-{id}">
                      <span>{$format('label.GCC_HIGH')}</span>
                      <Tooltip target="gcc-high-{id}" placement="top">
                        {$format('label.TOOLTIP.GCC_HIGH')}
                      </Tooltip>
                    </div>
                  {/if}

                  {#if data?.google?.aliases?.length}
                    <div class="section mt-3">{$format('label.GOOGLE_ALIASES')}</div>
                    <div class="d-flex flex-wrap justify-content-center gap-2 mt-2">
                      {#each data.google.aliases as alias, i (i)}
                        <div class="pill alias" id="alias-{id}-{i}">
                          <span>{alias}</span>
                          <Tooltip target="alias-{id}-{i}" placement="top">{alias}</Tooltip>
                        </div>
                      {/each}
                    </div>
                  {/if}

                  {#if !inactive && (data?.sigcompliance?.outreach || data?.sigcompliance?.hubspot || data?.usersettings?.autobcc || data?.sigcompliance?.frontapp || data?.sigcompliance?.salesforce)}
                    <div class="section mt-3">{$format('label.INTEGRATIONS')}</div>
                    <UserIntegrations bind:data />
                  {/if}

                  {#if data?.usersettings?.autobcc || data?.usersettings?.autotrackemails || data?.usersettings?.autotrackdocs || data?.usersettings?.autotracklinks}
                    <div class="section mt-1">{$format('label.FEATURES')}</div>
                    <div class="features">
                      {#if data?.usersettings?.autobcc}
                        <Icon name="at-sign" id="{id}-autobbc" class="cursor-help" />
                        <Tooltip target="{id}-autobbc" placement="top">
                          {$format('label.AUTO_BCC_ENABLED')}: {data.usersettings.autobcc}
                        </Tooltip>
                      {/if}

                      {#if data?.usersettings?.autotrackemails}
                        <Icon name="mail" id="{id}-autotrackemails" class="cursor-help" />
                        <Tooltip target="{id}-autotrackemails" placement="top">
                          {$format('label.EMAIL_TRACKING_ENABLED')}
                        </Tooltip>
                      {/if}

                      {#if data?.usersettings?.autotrackdocs}
                        <Icon name="paperclip" id="{id}-autotrackdocs" class="cursor-help" />
                        <Tooltip target="{id}-autotrackdocs" placement="top">
                          {$format('label.ATTACHMENT_TRACKING_ENABLED')}
                        </Tooltip>
                      {/if}

                      {#if data?.usersettings?.autotracklinks}
                        <Icon name="link" id="{id}-autotracklinks" class="cursor-help" />
                        <Tooltip target="{id}-autotracklinks" placement="top">
                          {$format('label.LINK_TRACKING_ENABLED')}
                        </Tooltip>
                      {/if}
                    </div>
                  {/if}

                  {#if inactive}
                    <div class="inactive mt-1">{$format('label.DISABLED_USER')}</div>
                  {/if}
                </div>
              </div>
            {/if}
            <div class="user-details">
              <TabContent>
                <TabPane tabId="signatures" tab={$format('label.SIGNATURES')} active>
                  <div class="pt-3">
                    <div class="fields">
                      <h2 class="heading">{$format('label.GENERAL')}</h2>
                      <div class="content">
                        {#if data?.sigcompliance?.block}
                          {@const block = data.sigcompliance.block}
                          <div class="field">
                            <small class="text-muted">
                              {$format('label.SIGNATURE_LAST_SEEN')}
                              {data?.lastsignature
                                ? ' (' +
                                  formatDistanceToNowStrict(new Date(data.lastsignature), {
                                    addSuffix: true
                                  }) +
                                  ')'
                                : ''}
                            </small>
                            <div class="text-muted pt-2 d-flex align-items-center flex-wrap gap-2">
                              <a
                                href="{basepath}/signatures/studio/{block}"
                                use:link
                                class="group text-muted rounded border px-2 py-1 gap-2">{block}</a
                              >
                            </div>
                          </div>
                        {:else}
                          <div class="field">
                            <small class="text-muted">{$format('label.SIGNATURE_LAST_SEEN')}</small>
                            <div class="pt-1">
                              <small class="text-muted">{$format('label.NO_LAST_SEEN_SIGNATURE')}</small>
                            </div>
                          </div>
                        {/if}
                        <hr />
                        <div class="field">
                          <small class="text-muted">{$format('label.GROUPS')}</small>
                          <div class="text-muted pt-2 d-flex align-items-center flex-wrap gap-2">
                            {#if assignedGroups.length}
                              {#each assignedGroups as group}
                                <a
                                  href="{basepath}/manage/groups/{group}"
                                  use:link
                                  class="group text-muted rounded border px-2 py-1 gap-2">{group}</a
                                >
                              {/each}
                            {:else}
                              <div class="pt-1">{$format('label.NO_ASSIGNED_GROUPS')}</div>
                            {/if}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="fields">
                      <h2 class="heading">
                        {$format('label.FIELDS')}
                        <Icon name="help-circle" id="fields-info" />
                        <Tooltip target="fields-info" placement="top">
                          {$format('label.TOOLTIP.FIELDS_INFO')}
                        </Tooltip>
                      </h2>
                      <div class="content pt-1">
                        <ParamFields bind:params={parameters} compliance={data?.sigcompliance} framed={false} />
                      </div>
                    </div>

                    <div class="fields">
                      <h2 class="heading">
                        {$format('label.PREVIEW')}
                        <Icon name="help-circle" id="signature-tooltip" />
                        <Tooltip target="signature-tooltip" placement="top">
                          {$format('label.TOOLTIP.PREVIEW_INFO')}
                        </Tooltip>
                      </h2>
                      <p class="pb-3">
                        {$format('label.PREVIEW_SIGNATURE_RECIPIENT')}
                        <b>
                          {$format('label.PREVIEW')}.
                        </b>
                      </p>
                      <div class="content">
                        <MySignature bind:email={user.email} zoomControls />
                      </div>
                    </div>

                    <DefaultSignatures {data} border />
                  </div>
                </TabPane>
                <TabPane tabId="settings" tab={$format('label.SETTINGS')}>
                  <div class="pt-3">
                    <Capability action="manage" subject="domainadmin">
                      <div class="fields">
                        <h2 class="heading">{$format('label.GENERAL')}</h2>
                        <div class="content pt-4">
                          <div class="field">
                            <MultiSelectField
                              id="roles"
                              label="Roles"
                              placeholder={$format('label.PLACEHOLDER.SELECT_ROLES')}
                              bind:options={usersRoles}
                              bind:selected={selectedUserRoles}
                              onUpdate={onRoleUpdate}
                            />
                          </div>

                          <div class="field read-only" class:inactive={!readOnlyApplies}>
                            <div class="read-only-name">
                              <div>
                                {$format('label.READ_ONLY')}
                                <Icon name="help-circle" id="{id}-read-only-help" />
                              </div>
                              <Tooltip target="{id}-read-only-help" placement="top">
                                {#if isDomainAdmin}
                                  {$format('label.READ_ONLY_EXEMPT')}
                                {:else if !readOnlyApplies}
                                  {$format('label.READ_ONLY_NOT_APPLICABLE')}
                                {:else}
                                  {$format('label.READ_ONLY_HELP')}
                                {/if}
                              </Tooltip>
                            </div>
                            <Switch bind:checked={readOnly} disabled={!readOnlyApplies} />
                          </div>
                        </div>
                      </div>
                    </Capability>
                    <div class="fields">
                      <h2 class="heading">{$format('label.FEATURES')}</h2>
                      <div class="content pt-2">
                        <UserFeatures bind:settings={usersettings} />
                      </div>
                    </div>

                    {#if usersettings.enablesales}
                      <div class="fields">
                        <div class="tour-user-notifications"></div>
                        <h2 class="heading">{$format('label.NOTIFICATIONS')}</h2>
                        <div class="content pt-2">
                          <UserNotifications bind:settings={usersettings} expandAll />
                        </div>
                      </div>
                      <div class="fields disable-user">
                        {#if !inactive}
                          <h2 class="heading">{$format('label.DISABLE_USER')}</h2>
                          <p>
                            {$format('label.DESCRIPTION.DISABLE_USER')}
                          </p>
                          <Button color="danger" class="mt-3" on:click={onToggleUserState}>
                            {$format('label.DISABLE_USER')}
                          </Button>
                        {:else}
                          <h2 class="heading">{$format('label.ENABLE_USER')}</h2>
                          <p>
                            {$format('label.DESCRIPTION.ENABLE_USER')}
                          </p>
                          <Button class="mt-3" on:click={onToggleUserState}>{$format('label.ENABLE_USER')}</Button>
                        {/if}
                      </div>
                    {/if}
                  </div>
                </TabPane>
                <TabPane tabId="directory" tab={$format('label.DIRECTORY')}>
                  <DirectorySettings {data} bind:selectedSyncData />
                </TabPane>
              </TabContent>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  {:else if !$query.isFetching}
    <div class="page-content">
      <Container fluid>
        <Row class="pt-3">
          <NoMatches message={$format('label.FAILURE.LOAD_USER')} />
        </Row>
      </Container>
    </div>
  {/if}
</div>

<Modal isOpen={selectedSyncData} fade={false} size="lg">
  <ModalHeader {toggle}>
    {user.name} - {selectedSyncData.dataLabel}
  </ModalHeader>
  <ModalBody>
    <p class="pb-3">
      {$format('label.USER_SYNC_LATEST', { values: { label: selectedSyncData.dataLabel } })}
    </p>
    <div class="snippet">
      <Clipboard text={JSON.stringify(selectedSyncData.data, null, 2)} let:copy on:copy={onCopy}>
        <button on:click={copy} class="copy-to-clipboard">{$format(copyText)}</button>
      </Clipboard>
      <Prism
        style="max-height: 420px;"
        showLineNumbers={true}
        language="js"
        code={JSON.stringify(selectedSyncData.data, null, 2)}
      />
    </div>
  </ModalBody>
</Modal>

<style lang="scss" src="./edit.scss"></style>
